/**
 * Fonte única de "meus pendentes" (tickets needs_action atribuídos a mim): a
 * contagem e os tickets mais recentes, numa consulta só. A contagem alimenta o
 * badge do título da aba, o item Tickets do menu e o "Precisa de ação" do
 * painel; os tickets são as notificações do sino do header.
 * Reusa a mesma queryKey da fase 1 → cache compartilhado com o painel, e as
 * mutations que invalidam 'tickets-needs-action-count' atualizam todos os consumidores.
 * Polling de 60s, **inclusive com a aba em background**: é justamente o caso "estou em
 * outra aba" que o alerta precisa cobrir (spec 2026-08-26 §8). O Chrome estrangula timers
 * de aba oculta para ~1x/min após 5 min, o que já casa com o intervalo — sem custo extra.
 *
 * Troca p/ backend: quando houver endpoint dedicado de contagem, troque fetchMyPending.
 */
import {useQuery} from 'react-query';
import {useAuth} from 'app/modules/auth';
import {serializeTicketFilters, needsMyAction} from '../ticketsHelpers';
import {listTickets} from '../requests/tickets';
import {FIXTURE_TICKETS} from '../ticketsFixtures';
import {Ticket} from '../models/Ticket';

// Sem a base da API (dev local), cai no fixture (mesma regra do TicketsQueriesContext).
const USE_FIXTURE = !process.env.REACT_APP_TICKETS_API_URL;

/** Quantos tickets o sino mostra; o total vem inteiro do `total` da lista. */
export const MY_PENDING_LIMIT = 20;

export type MyPending = {
  total: number;
  /** Os mais recentes primeiro (`updated_at`), até MY_PENDING_LIMIT. */
  items: Ticket[];
};

export const myPendingCountKey = (uid?: string) => [
  'tickets-needs-action-count',
  uid,
];

const byUpdatedDesc = (a: Ticket, b: Ticket) =>
  new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();

export const fetchMyPending = (uid: string): Promise<MyPending> => {
  if (USE_FIXTURE) {
    const mine = FIXTURE_TICKETS.filter((t) => needsMyAction(t, uid));
    return Promise.resolve({
      total: mine.length,
      items: [...mine].sort(byUpdatedDesc).slice(0, MY_PENDING_LIMIT),
    });
  }
  return listTickets(
    serializeTicketFilters({
      needs_action: true,
      assignee_uid: [uid],
      order: 'updated_at.desc',
      limit: MY_PENDING_LIMIT,
    })
  ).then((r) => ({total: r.total, items: r.items ?? []}));
};

/** A consulta inteira (o sino precisa dos tickets, do carregando e do erro). */
export const useMyPending = () => {
  const myUid = useAuth().currentUser?.uid;
  return useQuery({
    queryKey: myPendingCountKey(myUid),
    queryFn: () => fetchMyPending(myUid as string),
    enabled: Boolean(myUid),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
};

// Retorna number | undefined (undefined = carregando / sem uid). Consumidores fazem `?? 0`.
export const useMyPendingCount = (): number | undefined =>
  useMyPending().data?.total;
